import { ProjectInfoData } from '../../dto/EntityRepository';
import { CreateProjectInfoBulkReq, CreateProjectInfoReq, RemoveProjectInfoReq, UpdateProjectInfoBulkReq, UpdateReorderListReq } from '../../dto/ReqDtoRepository';
import { ProjectInfoListResponse, ProjectInfoResponse } from '../../dto/ResDtoRepository';
import { createProjectInfo, createProjectInfoBulk, deleteProjectInfo, getProjectInfoList, reorderProjectInfo, updateProjectInfoBulk } from '../../api/projectInfoApi';
import { useProjectInfoListStore } from '../../store/projectInfoStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { toUpdateProjectInfoBulkReq } from '../../util/projectInfoBulk';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';


interface UseProjectInfoStateResult {
  projectInfoList: ProjectInfoData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  listProjectId: string | null;
  status: ResourceStatus;
}

interface UseProjectInfoActionsResult {
  getProjectInfoList: (projectId: string) => Promise<void>;
  getProjectInfoListOwner: (projectId: string) => Promise<void>;
  createProjectInfo: (data: CreateProjectInfoReq) => Promise<void>;
  updateProjectInfoBulk: (data: UpdateProjectInfoBulkReq) => Promise<void>;
  /** hasChanged인 항목만 모아 bulk 수정. 없으면 요청하지 않는다. */
  updateChangedProjectInfoBulk: (projectId: string) => Promise<void>;
  deleteProjectInfo: (data: RemoveProjectInfoReq) => Promise<void>;
  reorderProjectInfo: (data: UpdateReorderListReq) => Promise<void>;
  createProjectInfoBulk: (aui: string, data: CreateProjectInfoBulkReq) => Promise<void>;
}

export const useProjectInfoState = (): UseProjectInfoStateResult => {
  const projectInfoList = useProjectInfoListStore((state) => state.projectInfoList);
  const listOwnerAui = useProjectInfoListStore((state) => state.listOwnerAui);
  const listScope = useProjectInfoListStore((state) => state.listScope);
  const listProjectId = useProjectInfoListStore((state) => state.listProjectId);
  const status = useProjectInfoListStore((state) => state.status);
  return { projectInfoList, listOwnerAui, listScope, listProjectId, status };
};

export const useProjectInfoActions = (): UseProjectInfoActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((state) => state.pageOwner?.aui);
  const setProjectInfoList = useProjectInfoListStore((state) => state.setProjectInfoList);
  const beginProjectInfoListFetch = useProjectInfoListStore((state) => state.beginProjectInfoListFetch);
  const setProjectInfoListWithScope = useProjectInfoListStore((state) => state.setProjectInfoListWithScope);
  const setProjectInfoListError = useProjectInfoListStore((state) => state.setProjectInfoListError);
  const afterCreateProjectInfo = useProjectInfoListStore((state) => state.afterCreateProjectInfo);
  const afterUpdateProjectInfoBulk = useProjectInfoListStore((state) => state.afterUpdateProjectInfoBulk);
  const afterDeleteProjectInfo = useProjectInfoListStore((state) => state.afterDeleteProjectInfo);
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const setDeletedTempAlert = useTempAlertStore((state) => state.setDeletedTempAlert);
  const withApiHandler = useApiWrapper();

  const handleProjectInfoListSuccess = (
    response: ProjectInfoListResponse,
    ownerAui: string,
    scope: ResourceScope,
    projectId: string,
  ) => {
    const projectInfoListData = response.data;
    setProjectInfoListWithScope(projectInfoListData, ownerAui, scope, projectId);
  };

  const handleCreateProjectInfoSuccess = (response: ProjectInfoResponse) => {
    const createdProjectElement = response.data;
    afterCreateProjectInfo(createdProjectElement);
  };

  const handleUpdateProjectInfoBulkSuccess = (response: ProjectInfoListResponse) => {
    afterUpdateProjectInfoBulk(response.data);
    setUpdatedTempAlert();
  };

  const handleDeleteProjectInfoSuccess = (projectInfoId: string) => {
    afterDeleteProjectInfo(projectInfoId);
    console.log("deleted well");
    setDeletedTempAlert();
  };

  const handleReorderProjectInfoSuccess = (response: ProjectInfoListResponse) => {
    const projectInfoListData = response.data;
    setProjectInfoList(projectInfoListData);
    setUpdatedTempAlert();
  };

  const handleCreateProjectInfoBulkSuccess = (response: ProjectInfoListResponse) => {
    const projectInfoListData = response.data;
    console.log("created project info bulk well", projectInfoListData);
    setUpdatedTempAlert();
  };

  const fetchProjectInfoList = async (aui: string, scope: ResourceScope, projectId: string) => {
    beginProjectInfoListFetch(aui, scope, projectId);
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => getProjectInfoList(aui, projectId),
        (response) => handleProjectInfoListSuccess(response, aui, scope, projectId),
        { matchOwnerAccess: false },
      );
    } catch (error) {
      if (useProjectInfoListStore.getState().status === 'loading') {
        setProjectInfoListError();
      }
      throw error;
    }
  };

  const handleProjectInfoRequest = async (
    action: 'get' | 'get_for_owner' | 'create' | 'create bulk' | 'update bulk' | 'delete' | 'reorder',
    projectId: string | null,
    data?: CreateProjectInfoReq | UpdateProjectInfoBulkReq | RemoveProjectInfoReq | UpdateReorderListReq | CreateProjectInfoBulkReq
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'create':
          return runCurrentArchiveRequest(
            aui,
            () => createProjectInfo(aui, data as CreateProjectInfoReq),
            handleCreateProjectInfoSuccess,
          );
        case 'create bulk':
          return runCurrentArchiveRequest(
            aui,
            () => createProjectInfoBulk(aui, data as CreateProjectInfoBulkReq),
            handleCreateProjectInfoBulkSuccess,
          );
        case 'update bulk':
          return runCurrentArchiveRequest(
            aui,
            () => updateProjectInfoBulk(aui, data as UpdateProjectInfoBulkReq),
            handleUpdateProjectInfoBulkSuccess,
          );
        case 'delete': {
          const projectInfoId = (data as RemoveProjectInfoReq).id;
          return runCurrentArchiveRequest(
            aui,
            () => deleteProjectInfo(aui, data as RemoveProjectInfoReq),
            () => handleDeleteProjectInfoSuccess(projectInfoId),
          );
        }
        case 'reorder':
          return runCurrentArchiveRequest(
            aui,
            () => reorderProjectInfo(aui, data as UpdateReorderListReq),
            handleReorderProjectInfoSuccess,
          );
        case 'get_for_owner':
          return fetchProjectInfoList(aui, 'owner', projectId as string);
        case 'get':
        default:
          return fetchProjectInfoList(aui, 'public', projectId as string);
      }
    };

    await withApiHandler(apiFunction, [aui, action, projectId, data]);
  };

  const handleProjectInfoRequestWithAui = async (
    aui: string,
    action: 'create bulk',
    data?: CreateProjectInfoBulkReq
  ) => {
    const apiFunction = async () => {
      switch (action) {
        case 'create bulk':
        default:
          handleCreateProjectInfoBulkSuccess(await createProjectInfoBulk(aui, data as CreateProjectInfoBulkReq));
          break;
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getProjectInfoListHandler = (projectId: string) => handleProjectInfoRequest('get', projectId);
  const getProjectInfoListOwnerHandler = (projectId: string) => handleProjectInfoRequest('get_for_owner', projectId);
  const createProjectInfoHandler = (data: CreateProjectInfoReq) => handleProjectInfoRequest('create', null, data);
  const updateProjectInfoBulkHandler = (data: UpdateProjectInfoBulkReq) => handleProjectInfoRequest('update bulk', null, data);
  const updateChangedProjectInfoBulkHandler = (projectId: string) => {
    const request = toUpdateProjectInfoBulkReq(
      projectId,
      useProjectInfoListStore.getState().projectInfoList,
    );
    if (!request) return Promise.resolve();
    return handleProjectInfoRequest('update bulk', null, request);
  };
  const deleteProjectInfoHandler = (data: RemoveProjectInfoReq) => handleProjectInfoRequest('delete', null, data);
  const reorderProjectInfoHandler = (data: UpdateReorderListReq) => handleProjectInfoRequest('reorder', null, data);
  const createProjectInfoBulkHandler = (aui: string, data: CreateProjectInfoBulkReq) => handleProjectInfoRequestWithAui(aui, 'create bulk', data);

  return {
    getProjectInfoList: getProjectInfoListHandler,
    getProjectInfoListOwner: getProjectInfoListOwnerHandler,
    createProjectInfo: createProjectInfoHandler,
    updateProjectInfoBulk: updateProjectInfoBulkHandler,
    updateChangedProjectInfoBulk: updateChangedProjectInfoBulkHandler,
    deleteProjectInfo: deleteProjectInfoHandler,
    reorderProjectInfo: reorderProjectInfoHandler,
    createProjectInfoBulk: createProjectInfoBulkHandler,
  };
};
